import { useMemo, useState } from 'react';
import { Inbox, AlertCircle, RefreshCw, X } from 'lucide-react';

import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  useBugReports,
  useBugReportReporters,
  useUpdateBugReport,
} from '@/features/bug-reports/useBugReports';
import type { BugReport, BugReportStatus } from '@/types/api';
import { cn } from '@/lib/utils';
import { useT, useLang, type StringKey } from '@/i18n';
import { formatRelative } from '@/lib/format';

/**
 * F5-T6c · Admin 10 — «Reportes de bugs» (contrato 2026-08-28).
 *
 * La bandeja al dibujo: 4 pills de estado con contador, lista a la izquierda
 * y PANEL FIJO de detalle a la derecha (el modal de detalle MUERE por
 * contrato: «sin modal para el detalle»). El estado se cambia desde el panel
 * con 4 botones aria-pressed; el cambio se anuncia por aria-live y el server
 * ya notifica por campana a quien reportó (maquinaria conservada).
 *
 * MUERE por contrato (sección dies): la severidad numérica como concepto de
 * UI (el dato vivo `severity` se conserva en el server; acá solo se lee
 * `blocking` como «me bloquea»), el campo de respuesta/nota de resolución
 * del panel («no hay campo de respuesta acá»), los adjuntos de captura
 * (nunca existieron) y el rojo en toda la ventana.
 *
 * SE CONSERVA (precedente E-23 — controles vivos que el contrato ni dibuja
 * ni mata): el filtro por reportador y por rango de fechas (fila discreta
 * bajo las pills) y el botón de refresco.
 *
 * NECESITA-SERVER: el estado «cuenta desactivada» del reportador (el
 * BugReport no trae flag de usuario activo; se muestra nombre y rol al
 * momento de reportar, que sí viajan en el registro).
 */

const PAGE_SIZE = 25;
/** Client-side working set: counters + filtro en vivo piden la lista entera.
 *  200 cubre con holgura el volumen real de la bandeja. */
const FETCH_LIMIT = 200;

type UiStatus = 'new' | 'review' | 'resolved' | 'nobug';

const STATUS_TO_UI: Record<BugReportStatus, UiStatus> = {
  open: 'new',
  triaged: 'review',
  resolved: 'resolved',
  wontfix: 'nobug',
};

const UI_TO_STATUS: Record<UiStatus, BugReportStatus> = {
  new: 'open',
  review: 'triaged',
  resolved: 'resolved',
  nobug: 'wontfix',
};

const STATUS_LABEL_KEY: Record<UiStatus, StringKey> = {
  new: 'f5t6c.bugs.status.new',
  review: 'f5t6c.bugs.status.review',
  resolved: 'f5t6c.bugs.status.resolved',
  nobug: 'f5t6c.bugs.status.nobug',
};

/** Chips de estado — catálogo cerrado del contrato (4 y nada más). */
const STATUS_CHIP: Record<UiStatus, string> = {
  new: 'bg-nav-amberTint text-nav-amberText border-[#e6cf9a]',
  review: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  resolved: 'bg-[#eef4ee] text-nav-sageText border-nav-sage',
  nobug: 'bg-nav-hair text-nav-slate border-nav-card',
};

const STATUS_DOT: Record<UiStatus, string> = {
  new: 'bg-nav-amber',
  review: 'bg-nav-teal',
  resolved: 'bg-nav-sage',
  nobug: 'bg-nav-faint',
};

/** Filtros dibujados: 4 pills. «No es un bug» NO tiene pill — se llega por «Todos». */
type FilterId = 'all' | 'new' | 'review' | 'resolved';

function firstLine(message: string): string {
  const line = message.split('\n')[0] ?? message;
  return line.length > 120 ? line.slice(0, 120) + '…' : line;
}

function restOfMessage(message: string): string {
  const idx = message.indexOf('\n');
  if (idx === -1) return message;
  const rest = message.slice(idx + 1).trim();
  return rest.length > 0 ? rest : message;
}

/** Parse liviano del user-agent: navegador + SO (dato real, del propio string). */
function parseUa(ua: string | null): string | null {
  if (!ua) return null;
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /Mac OS X|Macintosh/i.test(ua)
      ? 'macOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /iPhone|iPad|iOS/i.test(ua)
          ? 'iOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : null;
  let browser: string | null = null;
  const edge = ua.match(/Edg\/(\d+)/);
  const chrome = ua.match(/Chrome\/(\d+)/);
  const firefox = ua.match(/Firefox\/(\d+)/);
  const safari = ua.match(/Version\/(\d+).+Safari/);
  if (edge) browser = `Edge ${edge[1]}`;
  else if (chrome) browser = `Chrome ${chrome[1]}`;
  else if (firefox) browser = `Firefox ${firefox[1]}`;
  else if (safari) browser = `Safari ${safari[1]}`;
  return [browser, os].filter(Boolean).join(' · ') || null;
}

function roleLabel(role: string, t: ReturnType<typeof useT>): string {
  const key = `f5t6c.role.${role}` as StringKey;
  const label = t(key);
  return label === key ? role : label;
}

export function BugReportsPage(): JSX.Element {
  const t = useT();
  const lang = useLang();

  const [filter, setFilter] = useState<FilterId>('all');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState('');

  // Conservado (E-23): filtro por reportador y fechas — vivos, ni dibujados
  // ni matados por el contrato. Fila discreta, misma API.
  const [reporterId, setReporterId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, error, isFetching, refetch } = useBugReports(
    { reporterId: reporterId || '', from: from || '', to: to || '' },
    FETCH_LIMIT,
    0,
  );
  const reportersQ = useBugReportReporters();
  const updateM = useUpdateBugReport();

  const all = useMemo(() => {
    const items = [...(data?.items ?? [])];
    // Bloqueantes ORDENADOS PRIMERO (contrato); después, los más nuevos.
    items.sort((a, b) => {
      const ab = a.severity === 'blocking' ? 0 : 1;
      const bb = b.severity === 'blocking' ? 0 : 1;
      if (ab !== bb) return ab - bb;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return items;
  }, [data]);

  const counts = useMemo(() => {
    const c: Record<FilterId, number> = { all: all.length, new: 0, review: 0, resolved: 0 };
    for (const r of all) {
      const ui = STATUS_TO_UI[r.status];
      if (ui === 'new') c.new += 1;
      else if (ui === 'review') c.review += 1;
      else if (ui === 'resolved') c.resolved += 1;
    }
    return c;
  }, [all]);

  const filtered = useMemo(
    () => (filter === 'all' ? all : all.filter((r) => STATUS_TO_UI[r.status] === filter)),
    [all, filter],
  );

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const selected = useMemo(
    () => filtered.find((r) => r.id === selectedId) ?? visible[0] ?? null,
    [filtered, selectedId, visible],
  );

  const pickFilter = (f: FilterId): void => {
    setFilter(f);
    setPage(0);
    setSelectedId(null);
  };

  const changeStatus = (report: BugReport, ui: UiStatus): void => {
    const next = UI_TO_STATUS[ui];
    if (report.status === next || updateM.isPending) return;
    updateM.mutate(
      { id: report.id, body: { status: next } },
      {
        onSuccess: () => {
          setLiveMsg(t('f5t6c.bugs.aria.statusChanged', { status: t(STATUS_LABEL_KEY[ui]) }));
        },
      },
    );
  };

  const pageLabel =
    filtered.length === 1
      ? t('f5t6c.bugs.pageLabelOne', { page: safePage + 1, pages })
      : t('f5t6c.bugs.pageLabel', { n: filtered.length, page: safePage + 1, pages });

  const filterPills: Array<{ id: FilterId; label: string; count: number }> = [
    { id: 'all', label: t('f5t6c.bugs.filter.all'), count: counts.all },
    { id: 'new', label: t('f5t6c.bugs.filter.new'), count: counts.new },
    { id: 'review', label: t('f5t6c.bugs.filter.review'), count: counts.review },
    { id: 'resolved', label: t('f5t6c.bugs.filter.resolved'), count: counts.resolved },
  ];

  return (
    <div className="max-w-[1440px]">
      {/* aria-live: el cambio de estado se anuncia (contrato a11y). */}
      <p aria-live="polite" role="status" className="sr-only">
        {liveMsg}
      </p>

      <header className="max-w-[760px] mb-6">
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight text-nav-navy mb-2">
          {t('f5t6c.bugs.title')}
        </h1>
        <p className="font-body text-[15px] leading-relaxed text-nav-navy/90 m-0">
          {t('f5t6c.bugs.subtitle')}
        </p>
        <p className="font-body text-[14px] text-nav-slate mt-2 m-0">{t('f5t6c.bugs.note')}</p>
      </header>

      <section className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-8 pt-7 pb-[34px] animate-fade-up-slow motion-reduce:animate-none">
        {/* Fila de filtros por estado (pills con contador, aria-pressed). */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {filterPills.map((p) => {
            const active = filter === p.id;
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={active}
                onClick={() => pickFilter(p.id)}
                className={cn(
                  'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                  active
                    ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                    : 'bg-white border-nav-line text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark',
                )}
              >
                {p.label}
                <span className={cn('tabular-nums', active ? 'text-white/75' : 'text-nav-faint')}>
                  {p.count}
                </span>
              </button>
            );
          })}

          {/* Conservado (E-23): refresco — vivo, ni dibujado ni matado. */}
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="ml-auto inline-flex items-center gap-1.5 min-h-[44px] px-[14px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {t('admin.bugReports.refresh')}
          </button>
        </div>

        {/* Conservado (E-23): reportador + fechas, fila discreta. */}
        <div className="flex items-end gap-2 flex-wrap mb-[22px]">
          <label className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1">
              {t('admin.bugReports.filter.reporter')}
            </span>
            <Select
              value={reporterId}
              onChange={(e) => {
                setReporterId(e.target.value);
                setPage(0);
              }}
              className="min-h-[44px] max-w-[220px]"
            >
              <option value="">{t('admin.bugReports.filter.reporterAll')}</option>
              {(reportersQ.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1">
              {t('admin.bugReports.filter.from')}
            </span>
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
              className="min-h-[44px]"
            />
          </label>
          <label className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1">
              {t('admin.bugReports.filter.to')}
            </span>
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
              className="min-h-[44px]"
            />
          </label>
          {(reporterId || from || to) && (
            <button
              type="button"
              onClick={() => {
                setReporterId('');
                setFrom('');
                setTo('');
                setPage(0);
              }}
              className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg font-body text-[12.5px] text-nav-slate hover:bg-[#f2f5f7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              {t('admin.bugReports.filter.clear')}
            </button>
          )}
        </div>

        {error ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <ErrorState
              icon={<AlertCircle className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.bugs.error.title')}
              body={t('f5t6c.bugs.error.body')}
              action={{ label: t('f5t6c.bugs.error.retry'), onClick: () => refetch() }}
            />
          </div>
        ) : isLoading ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-4 space-y-2" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[74px] w-full" />
            ))}
          </div>
        ) : all.length === 0 ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            {/* «El buen caso», en salvia, SIN botón (contrato states 1). */}
            <EmptyState
              icon={<Inbox className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.bugs.empty.title')}
              body={t('f5t6c.bugs.empty.body')}
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <EmptyState
              icon={<Inbox className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.bugs.emptyFilter.title')}
              body={t('f5t6c.bugs.emptyFilter.body')}
              action={{ label: t('f5t6c.bugs.emptyFilter.cta'), onClick: () => pickFilter('all') }}
            />
          </div>
        ) : (
          <div className="flex items-start gap-4 flex-wrap">
            {/* LISTA */}
            <div className="flex-1 min-w-[320px] bg-white border-[1.5px] border-nav-card rounded-[12px] overflow-hidden">
              <ul>
                {visible.map((r, i) => {
                  const ui = STATUS_TO_UI[r.status];
                  const blocking = r.severity === 'blocking';
                  const isSel = selected?.id === r.id;
                  return (
                    <li key={r.id} className={i === 0 ? '' : 'border-t border-nav-hair'}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(r.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedId(r.id);
                          }
                        }}
                        aria-current={isSel || undefined}
                        style={{ animationDelay: `${i * 50}ms` }}
                        className={cn(
                          'flex items-center gap-3.5 min-h-[74px] px-[22px] py-4 cursor-pointer border-l-[3px] transition-colors duration-150',
                          'animate-fade-up motion-reduce:animate-none',
                          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:-outline-offset-2',
                          isSel
                            ? 'border-l-nav-teal bg-nav-tealTint hover:bg-[#d3e8e9]'
                            : 'border-l-transparent hover:bg-[#f4fafa]',
                        )}
                      >
                        {/* El punto de una fila bloqueante es ÁMBAR aunque su
                            estado tenga otro dot (único ámbar-alerta de la ventana). */}
                        <span
                          aria-hidden
                          className={cn(
                            'w-2.5 h-2.5 rounded-full flex-none',
                            blocking ? 'bg-nav-amber' : STATUS_DOT[ui],
                          )}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block font-head text-[13.5px] font-bold tracking-[-0.01em] text-nav-navy truncate">
                            {firstLine(r.message)}
                          </span>
                          <span className="block font-body text-[12.5px] text-nav-slate tabular-nums truncate">
                            {r.userName} · {roleLabel(r.userRole, t)} · {formatRelative(r.createdAt)}
                            {blocking ? ` · ${t('f5t6c.bugs.blocksMe')}` : ''}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'flex-none inline-flex items-center rounded-full border px-[11px] py-1 font-head text-[11.5px] font-bold',
                            STATUS_CHIP[ui],
                          )}
                        >
                          {t(STATUS_LABEL_KEY[ui])}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="px-[22px] py-4 border-t border-nav-hair">
                <p className="font-body text-[13px] text-nav-slate tabular-nums m-0">{pageLabel}</p>
                <Pager
                  page={safePage}
                  pages={pages}
                  onPage={setPage}
                  prevLabel={t('f5t6c.pager.prev')}
                  nextLabel={t('f5t6c.pager.next')}
                  pageLabel={pageLabel}
                />
              </div>
            </div>

            {/* PANEL DE DETALLE fijo — sin modal, sin navegación. */}
            {selected && (
              <DetailPanel
                report={selected}
                lang={lang}
                t={t}
                pending={updateM.isPending}
                onStatus={(ui) => changeStatus(selected, ui)}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function DetailPanel({
  report,
  lang,
  t,
  pending,
  onStatus,
}: {
  report: BugReport;
  lang: 'en' | 'es';
  t: ReturnType<typeof useT>;
  pending: boolean;
  onStatus: (ui: UiStatus) => void;
}): JSX.Element {
  const ui = STATUS_TO_UI[report.status];
  const created = new Date(report.createdAt);
  const locale = lang === 'es' ? 'es' : 'en';
  const when = created.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = created.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // «Lo que adjuntó el sistema»: navegador+SO · pantalla {ruta} · rol · hora.
  const techParts: string[] = [];
  const ua = parseUa(report.userAgent);
  if (ua) techParts.push(ua);
  techParts.push(
    `${t('f5t6c.bugs.tech.screen')} ${report.pageUrl ?? t('f5t6c.bugs.tech.noSession')}`,
    `${t('f5t6c.bugs.tech.role')} ${roleLabel(report.userRole, t)}`,
    `${when} ${time}`,
  );

  const statusButtons: UiStatus[] = ['new', 'review', 'resolved', 'nobug'];

  return (
    <aside className="w-[400px] flex-none min-w-[320px] bg-white border-[1.5px] border-nav-card rounded-[12px] px-6 py-[22px]">
      <div className="flex items-start justify-between gap-3 mb-5">
        <h2 className="font-head text-[16px] font-bold text-nav-navy m-0 min-w-0">
          {firstLine(report.message)}
        </h2>
        <span
          className={cn(
            'flex-none inline-flex items-center rounded-full border px-[11px] py-1 font-head text-[11.5px] font-bold',
            STATUS_CHIP[ui],
          )}
        >
          {t(STATUS_LABEL_KEY[ui])}
        </span>
      </div>

      <div className="flex flex-col gap-3.5 mb-5">
        <div>
          <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1">
            {t('f5t6c.bugs.panel.who')}
          </p>
          {/* NECESITA-SERVER: «cuenta desactivada» del reportador — el
              BugReport no trae flag de usuario activo. Nombre y rol son los
              del momento de reportar (viajan en el registro, se conservan). */}
          <p className="font-body text-[13.5px] font-semibold text-nav-navy m-0">
            {report.userName} · {roleLabel(report.userRole, t)}
          </p>
        </div>
        <div>
          <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1">
            {t('f5t6c.bugs.panel.where')}
          </p>
          <p className="font-body text-[13.5px] text-nav-navy m-0 break-all">
            {report.pageUrl ?? t('f5t6c.bugs.panel.noPage')}
          </p>
        </div>
        <div>
          <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1">
            {t('f5t6c.bugs.panel.what')}
          </p>
          <p className="font-body text-[13.5px] leading-[1.6] text-nav-navy m-0 whitespace-pre-wrap break-words">
            {restOfMessage(report.message)}
          </p>
        </div>
        <div>
          <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1">
            {t('f5t6c.bugs.panel.when')}
          </p>
          <p className="font-body text-[13.5px] text-nav-navy tabular-nums m-0">
            {when} {time}
          </p>
        </div>
      </div>

      <div className="bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[12px] px-4 py-3.5 mb-5">
        <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1">
          {t('f5t6c.bugs.panel.tech')}
        </p>
        <p className="font-body text-[12.5px] text-nav-slate tabular-nums m-0 break-words">
          {techParts.join(' · ')}
        </p>
      </div>

      <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0">
        {t('f5t6c.bugs.panel.status')}
      </p>
      <div className="flex items-center gap-2 flex-wrap mt-[9px] mb-4">
        {statusButtons.map((s) => {
          const active = ui === s;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={active}
              disabled={pending}
              onClick={() => onStatus(s)}
              className={cn(
                'inline-flex items-center min-h-[44px] px-3.5 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors duration-150 disabled:opacity-60',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                active
                  ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                  : 'bg-white border-nav-line text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark',
              )}
            >
              {t(STATUS_LABEL_KEY[s])}
            </button>
          );
        })}
      </div>

      {/* Sin campo de respuesta, a propósito (contrato): si hace falta hablar,
          se habla por chat. La nota de resolución del diseño viejo MUERE acá. */}
      <p className="font-body text-[12.5px] text-nav-slate leading-relaxed m-0">
        {t('f5t6c.bugs.panel.note')}
      </p>
    </aside>
  );
}
